import React, { useMemo } from 'react';
import styled from 'styled-components';
import { PremiumDatePicker } from './Text';
import { adjustLastNameCasing } from '../../utils/nameCasing';

export interface EsqTextFormValues {
  fullName?: string;
  firstName: string;
  lastName: string;
  dob: string;
  dod: string;
  verse: string;
  verse2?: string;
  verse3?: string;
  abbreviateMonth?: boolean;
  yearOnly?: boolean;
  hideDeathDate?: boolean;
}

interface EsqTextProps {
  value: EsqTextFormValues;
  onChange: (next: EsqTextFormValues) => void;
  limits?: Partial<Record<keyof EsqTextFormValues, number>>;
  fullNameMode?: boolean;
  sharedLimit?: number;
  isSerptopMode?: boolean;
}

const Wrap = styled.div`
  display: grid;
  gap: 10px;
  font-family: 'Inter', 'Segoe UI', Arial, sans-serif;
`;

const Label = styled.label`
  display: grid;
  gap: 6px;
  font-size: 12px;
  font-weight: 700;
  color: #334155;
  text-transform: uppercase;
  letter-spacing: 0.4px;
`;

const Input = styled.input`
  width: 100%;
  border: 1px solid #dbe3ef;
  border-radius: 8px;
  padding: 10px 12px;
  font-size: 14px;
  font-family: 'Inter', 'Segoe UI', Arial, sans-serif;
  color: #0f172a;
  outline: none;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;

  &:focus {
    border-color: #5d722c;
    box-shadow: 0 0 0 2px rgba(93, 114, 44, 0.15);
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  border: 1px solid #dbe3ef;
  border-radius: 8px;
  padding: 10px 12px;
  font-size: 14px;
  font-family: 'Inter', 'Segoe UI', Arial, sans-serif;
  color: #0f172a;
  min-height: 96px;
  resize: vertical;
  outline: none;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;

  &:focus {
    border-color: #5d722c;
    box-shadow: 0 0 0 2px rgba(93, 114, 44, 0.15);
  }
`;

const Hint = styled.p`
  margin: 0;
  font-size: 12px;
  line-height: 1.4;
  color: #64748b;
`;

const MONTHS = [
  'JANUARY', 'FEBRUARY', 'MARCH', 'APRIL', 'MAY', 'JUNE',
  'JULY', 'AUGUST', 'SEPTEMBER', 'OCTOBER', 'NOVEMBER', 'DECEMBER'
];

const EsqText: React.FC<EsqTextProps> = ({ value, onChange, limits, fullNameMode, sharedLimit, isSerptopMode = true }) => {
  const patch = (partial: Partial<EsqTextFormValues>) => onChange({ ...value, ...partial });
  const abbreviateMonth = value.abbreviateMonth !== false;
  const yearOnly = value.yearOnly === true;
  const hideDeathDate = value.hideDeathDate === true;

  const toDate = (v: string, fallback?: Date): Date | null => {
    const raw = String(v || '').trim();
    if (!raw) return fallback || null;
    if (/^\d{4}$/.test(raw)) {
      return new Date(Number(raw), 0, 1);
    }
    const parsed = new Date(raw);
    if (Number.isNaN(parsed.getTime())) return fallback || null;
    return parsed;
  };

  const formatDisplayDate = (date: Date | null, fallback: string, abbreviate = true, isYearOnly = false): string => {
    if (!date || Number.isNaN(date.getTime())) return fallback;
    if (isYearOnly) {
      return String(date.getFullYear());
    }
    const longMonthLabels = ['JANUARY', 'FEBRUARY', 'MARCH', 'APRIL', 'MAY', 'JUNE', 'JULY', 'AUGUST', 'SEPTEMBER', 'OCTOBER', 'NOVEMBER', 'DECEMBER'];
    const shortMonthLabels = ['JAN.', 'FEB.', 'MAR.', 'APR.', 'MAY', 'JUNE', 'JULY', 'AUG.', 'SEPT.', 'OCT.', 'NOV.', 'DEC.'];
    const month = (abbreviate ? shortMonthLabels : longMonthLabels)[date.getMonth()] || '';
    return `${month} ${date.getDate()}, ${date.getFullYear()}`;
  };

  const toStorageDateString = (date: Date, abbreviate = true, isYearOnly = false) =>
    formatDisplayDate(date, '', abbreviate, isYearOnly).toUpperCase();

  const dobDate = useMemo(() => toDate(value.dob, new Date(1940, 0, 1)), [value.dob]);
  const dodDate = useMemo(() => toDate(value.dod), [value.dod]);

  const toPickerParts = (date: Date | null, fallback: Date) => {
    const target = date && !Number.isNaN(date.getTime()) ? date : fallback;
    return {
      month: MONTHS[target.getMonth()],
      day: String(target.getDate()),
      year: String(target.getFullYear())
    };
  };

  const applyPickerChange = (field: 'dob' | 'dod', month: string, day: string, year: string) => {
    const monthIdx = MONTHS.indexOf(String(month || '').toUpperCase());
    const dayNum = Number(day);
    const yearNum = Number(year);
    if (!Number.isFinite(yearNum)) return;
    const m = monthIdx >= 0 ? monthIdx : 0;
    const d = Number.isFinite(dayNum) && dayNum > 0 ? dayNum : 1;
    const nextDate = new Date(yearNum, m, d);
    const dateStr = toStorageDateString(nextDate, abbreviateMonth, yearOnly);
    if (field === 'dod') {
      patch({ dod: dateStr, hideDeathDate: false });
    } else {
      patch({ [field]: dateStr });
    }
  };

  const dobParts = toPickerParts(dobDate, new Date(1940, 0, 1));
  const dodParts = toPickerParts(dodDate, new Date());

  const renderDateFields = () => (
    <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start', width: '100%', marginBottom: '10px' }}>
      {/* Left side: Date fields */}
      <div style={{ width: 'calc(70% - 6px)', display: 'grid', gap: '10px', flexShrink: 0 }}>
        <Label>
          Date of Birth
          <PremiumDatePicker
            month={dobParts.month}
            day={dobParts.day}
            year={dobParts.year}
            onChange={(month, day, year) => applyPickerChange('dob', month, day, year)}
            maxDate={new Date()}
            abbreviateMonth={abbreviateMonth}
            yearOnly={yearOnly}
          />
        </Label>

        <Label>
          Date of Death
          <PremiumDatePicker
            month={dodParts.month}
            day={dodParts.day}
            year={dodParts.year}
            disabled={hideDeathDate}
            onChange={(month, day, year) => applyPickerChange('dod', month, day, year)}
            minDate={dobDate || undefined}
            maxDate={new Date()}
            abbreviateMonth={abbreviateMonth}
            yearOnly={yearOnly}
          />
        </Label>
        <div style={{ display: 'flex', alignItems: 'center', marginTop: '2px', padding: '6px 8px', background: '#f8f9fa', borderRadius: '6px', border: '1px solid #e9ecef' }}>
          <input
            id="esqHideDeathDate"
            type="checkbox"
            checked={hideDeathDate}
            onChange={(e) => {
              const nextHide = e.target.checked;
              const currentDod = value.dod || toStorageDateString(dodDate || new Date(), abbreviateMonth, yearOnly);
              patch({
                hideDeathDate: nextHide,
                dod: currentDod
              });
            }}
            style={{ marginRight: '8px', width: '16px', height: '16px', accentColor: '#5D722C', flexShrink: 0 }}
          />
          <label htmlFor="esqHideDeathDate" style={{ fontSize: '11px', color: '#495057', cursor: 'pointer', fontWeight: 500 }}>
            I don't want death date
          </label>
        </div>
      </div>

      {/* Right side: Checkboxes */}
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
        padding: '8px',
        background: '#f8f9fa',
        borderRadius: '6px',
        border: '1px solid #e9ecef',
        width: 'calc(30% - 6px)',
        boxSizing: 'border-box',
        marginTop: '23px',
        flexShrink: 0
      }}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <input
            id="esqAbbreviateMonth"
            type="checkbox"
            checked={abbreviateMonth}
            onChange={(e) => {
              const nextAbbrev = e.target.checked;
              const next: Partial<EsqTextFormValues> = { abbreviateMonth: nextAbbrev };
              if (dobDate) next.dob = toStorageDateString(dobDate, nextAbbrev, yearOnly);
              if (dodDate && !hideDeathDate) next.dod = toStorageDateString(dodDate, nextAbbrev, yearOnly);
              patch(next);
            }}
            style={{ marginRight: '8px', width: '16px', height: '16px', accentColor: '#5D722C', flexShrink: 0 }}
          />
          <label htmlFor="esqAbbreviateMonth" style={{ fontSize: '11px', color: '#495057', cursor: 'pointer', fontWeight: 500, lineHeight: 1.4 }}>
            Abbreviate Month<br />(JAN. vs JANUARY)
          </label>
        </div>

        <div style={{ display: 'flex', alignItems: 'center' }}>
          <input
            id="esqYearOnly"
            type="checkbox"
            checked={yearOnly}
            onChange={(e) => {
              const nextYearOnly = e.target.checked;
              const next: Partial<EsqTextFormValues> = { yearOnly: nextYearOnly };
              if (dobDate) next.dob = toStorageDateString(dobDate, abbreviateMonth, nextYearOnly);
              if (dodDate && !hideDeathDate) next.dod = toStorageDateString(dodDate, abbreviateMonth, nextYearOnly);
              patch(next);
            }}
            style={{ marginRight: '8px', width: '16px', height: '16px', accentColor: '#5D722C', flexShrink: 0 }}
          />
          <label htmlFor="esqYearOnly" style={{ fontSize: '11px', color: '#495057', cursor: 'pointer', fontWeight: 500, lineHeight: 1.4 }}>
            Year only
          </label>
        </div>
      </div>
    </div>
  );

  return (
    <Wrap>
      {fullNameMode ? (
        <Label>
          Full Name
          <Input
            value={String(value.fullName ?? '')}
            onChange={(e) => patch({ fullName: adjustLastNameCasing(e.target.value) })}
            maxLength={(limits as any)?.fullName}
            placeholder="Enter full name"
          />
        </Label>
      ) : (
        <>
          <Label>
            First Name
            <Input
              value={value.firstName}
              onChange={(e) => patch({ firstName: e.target.value.toUpperCase() })}
              maxLength={sharedLimit ? Math.max(0, sharedLimit - (value.lastName ? value.lastName.length + 1 : 0)) : limits?.firstName}
              placeholder="Enter first name"
            />
          </Label>

          <Label>
            Last Name
            <Input
              value={value.lastName}
              onChange={(e) => patch({ lastName: adjustLastNameCasing(e.target.value) })}
              maxLength={sharedLimit ? Math.max(0, sharedLimit - (value.firstName ? value.firstName.length + 1 : 0)) : limits?.firstName}
              placeholder="Enter last name"
            />
          </Label>
        </>
      )}

      {renderDateFields()}

      <Label>
        {isSerptopMode ? 'Verse 1' : 'Verse'}
        <TextArea
          value={value.verse || ''}
          onChange={(e) => patch({ verse: e.target.value })}
          maxLength={limits?.verse}
          placeholder={isSerptopMode ? "Enter verse 1 text" : "Enter verse text"}
          rows={2}
          style={{ minHeight: isSerptopMode ? '54px' : '80px' }}
        />
      </Label>

      {isSerptopMode && (
        <>
          <Label>
            Verse 2
            <TextArea
              value={value.verse2 || ''}
              onChange={(e) => patch({ verse2: e.target.value })}
              maxLength={(limits as any)?.verse2 || limits?.verse}
              placeholder="Enter verse 2 text"
              rows={2}
              style={{ minHeight: '54px' }}
            />
          </Label>

          <Label>
            Verse 3
            <TextArea
              value={value.verse3 || ''}
              onChange={(e) => patch({ verse3: e.target.value })}
              maxLength={(limits as any)?.verse3 || limits?.verse}
              placeholder="Enter verse 3 text"
              rows={2}
              style={{ minHeight: '54px' }}
            />
          </Label>
        </>
      )}

      <Hint>Saved text is reused automatically when you switch ESQ templates.</Hint>
    </Wrap>
  );
};

export default EsqText;
